import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { getFinancialYear } from '@/utils/financialYearUtils';
import { calculateMultiYearEmissions } from '@/utils/emissionCalculator';

/**
 * Hook to manage emissions data lifecycle.
 */
export const useEmissionsData = (currentOrg, user, userData = null) => {
    const [emissions, setEmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingEntry, setDeletingEntry] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [entryHistory, setEntryHistory] = useState([]);
    const [editModalLoading, setEditModalLoading] = useState(false);

    const fetchEmissionsWithDocs = useCallback(async () => {
        if (!currentOrg?.id) {
            setLoading(false);
            return;
        }
        try {
            const { data: emissionsData, error } = await supabase
                .from('emissions_data')
                .select(`
          *,
          documents(id, filename, storage_path, document_type, file_size)
        `)
                .eq('organisation_id', currentOrg.id)
                .order('entry_date', { ascending: false });

            if (error) throw error;

            const transformedEmissions = (emissionsData || []).map(e => ({
                ...e,
                evidence_files: e.documents || []
            }));

            setEmissions(transformedEmissions);
        } catch (error) {
            console.error('Error fetching emissions:', error);
            toast.error('Failed to load emissions data');
        } finally {
            setLoading(false);
        }
    }, [currentOrg?.id]);

    const handleDeleteEntry = async (emissionId, onSuccess) => {
        setDeletingEntry(emissionId);
        try {
            // Delete associated documents first
            const { data: docs } = await supabase
                .from('documents')
                .select('storage_path')
                .eq('emission_entry_id', emissionId);

            if (docs && docs.length > 0) {
                const filePaths = docs.map(d => d.storage_path).filter(Boolean);
                if (filePaths.length > 0) {
                    await supabase.storage.from('evidence-vault').remove(filePaths);
                }
                await supabase.from('documents').delete().eq('emission_entry_id', emissionId);
            }

            const { error } = await supabase
                .from('emissions_data')
                .delete()
                .eq('id', emissionId);

            if (error) throw error;

            if (currentOrg?.entry_count > 0) {
                await supabase
                    .from('organisations')
                    .update({ entry_count: currentOrg.entry_count - 1 })
                    .eq('id', currentOrg.id);
            }

            toast.success('Entry deleted successfully');
            if (onSuccess) onSuccess();
            fetchEmissionsWithDocs();
        } catch (error) {
            console.error('Error deleting entry:', error);
            toast.error(error.message || 'Failed to delete entry');
        } finally {
            setDeletingEntry(null);
        }
    };

    const fetchEntryHistory = async (emissionId) => {
        setHistoryLoading(true);
        try {
            const { data, error } = await supabase
                .from('entry_history')
                .select('*')
                .eq('emission_entry_id', emissionId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setEntryHistory(data || []);
            return data || [];
        } catch (error) {
            console.error('Error fetching entry history:', error);
            toast.error('Failed to load entry history');
            return [];
        } finally {
            setHistoryLoading(false);
        }
    };

    const addEmissionEntry = async (formData, categoryInfo, onSuccess) => {
        try {
            let internalUserData = userData;
            if (!internalUserData) {
                const { data: userById } = await supabase
                    .from('users')
                    .select('id')
                    .eq('supabase_user_id', user?.id)
                    .single();
                internalUserData = userById;
            }

            const stateOrGrid = currentOrg?.electricity_grid_region || currentOrg?.state || 'NSW';
            const startDate = formData.start_date || formData.entry_date;
            const endDate = formData.is_date_range ? (formData.end_date || startDate) : startDate;
            const primaryFY = getFinancialYear(new Date(startDate));

            const multiYearResult = calculateMultiYearEmissions(
                formData.category,
                parseFloat(formData.quantity),
                stateOrGrid,
                startDate,
                endDate
            );

            const calcResult = {
                co2_equivalent: multiYearResult.total_co2e_t,
                factor_value: multiYearResult.allocations.length > 0 ? multiYearResult.allocations[0].factor : null,
                factor_source: multiYearResult.allocations.length > 0
                    ? multiYearResult.allocations.map(a => a.factor_source).join(', ')
                    : 'NGA Factors'
            };

            const entryId = crypto.randomUUID();
            const entryDateTime = new Date().toISOString();

            const { data: newEntry, error: insertError } = await supabase
                .from('emissions_data')
                .insert({
                    id: entryId,
                    organisation_id: currentOrg.id,
                    user_id: internalUserData?.id,
                    category: formData.category,
                    quantity: parseFloat(formData.quantity),
                    unit: categoryInfo.unit,
                    entry_date: entryDateTime,
                    start_date: startDate,
                    end_date: endDate,
                    total_tco2e: calcResult.co2_equivalent,
                    financial_year: primaryFY,
                    co2_equivalent: calcResult.co2_equivalent,
                    factor_value_used: calcResult.factor_value,
                    factor_source: calcResult.factor_source,
                    notes: formData.notes || null
                })
                .select()
                .single();

            if (insertError) throw insertError;

            await supabase
                .from('organisations')
                .update({ entry_count: (currentOrg.entry_count || 0) + 1 })
                .eq('id', currentOrg.id);

            // Handle receipt upload separately if needed
            if (formData.receipt_file) {
                const fileExt = formData.receipt_file.name.split('.').pop();
                const filePath = `${currentOrg.id}/${entryId}/receipt.${fileExt}`;
                await supabase.storage.from('evidence-vault').upload(filePath, formData.receipt_file);
                await supabase.from('documents').insert({
                    id: crypto.randomUUID(),
                    organisation_id: currentOrg.id,
                    user_id: internalUserData?.id,
                    emission_entry_id: entryId,
                    filename: formData.receipt_file.name,
                    file_type: formData.receipt_file.type,
                    file_size: formData.receipt_file.size,
                    storage_path: filePath,
                    document_type: 'receipt'
                });
            }

            await supabase.from('entry_history').insert({
                id: crypto.randomUUID(),
                emission_entry_id: entryId,
                organisation_id: currentOrg.id,
                user_id: internalUserData?.id,
                action: 'created',
                category: formData.category,
                quantity: parseFloat(formData.quantity),
                unit: categoryInfo.unit,
                co2_equivalent: calcResult.co2_equivalent,
                entry_date: startDate,
                factor_value_used: calcResult.factor_value,
                factor_year_used: primaryFY,
                notes: formData.notes || null,
                user_email: user?.email
            });

            await supabase.from('audit_logs').insert({
                id: crypto.randomUUID(),
                user_id: internalUserData?.id,
                user_email: user?.email,
                organisation_id: currentOrg.id,
                resource_type: 'emission',
                resource_id: entryId,
                action: 'create',
                description: `Added ${categoryInfo?.label || formData.category}: ${formData.quantity} ${categoryInfo?.unit || ''} (${calcResult.co2_equivalent.toFixed(4)} tCO₂-e)`
            });

            toast.success('Entry added successfully!');
            if (onSuccess) onSuccess();
            fetchEmissionsWithDocs();
            return newEntry;
        } catch (error) {
            console.error('Error adding emission:', error);
            toast.error(error.message || 'Failed to add entry');
            throw error;
        }
    };

    const updateEmissionEntry = async (emissionId, updateData, categoryInfo, originalEntry, onSuccess) => {
        setEditModalLoading(true);
        try {
            let internalUserData = userData;
            if (!internalUserData) {
                const { data: userById } = await supabase
                    .from('users')
                    .select('id')
                    .eq('supabase_user_id', user?.id)
                    .single();
                internalUserData = userById;
            }

            const stateOrGrid = currentOrg?.electricity_grid_region || currentOrg?.state || 'NSW';
            const startDate = updateData.start_date || updateData.entry_date || originalEntry.start_date;
            const endDate = updateData.is_date_range ? (updateData.end_date || startDate) : startDate;
            const primaryFY = getFinancialYear(new Date(startDate));

            const multiYearResult = calculateMultiYearEmissions(
                updateData.category,
                parseFloat(updateData.quantity),
                stateOrGrid,
                startDate,
                endDate
            );

            const calcResult = {
                co2_equivalent: multiYearResult.total_co2e_t,
                factor_value: multiYearResult.allocations.length > 0 ? multiYearResult.allocations[0].factor : null,
                factor_source: multiYearResult.allocations.length > 0
                    ? multiYearResult.allocations.map(a => a.factor_source).join(', ')
                    : 'NGA Factors'
            };

            const { error: updateError } = await supabase
                .from('emissions_data')
                .update({
                    category: updateData.category,
                    quantity: parseFloat(updateData.quantity),
                    unit: categoryInfo?.unit || '',
                    start_date: startDate,
                    end_date: endDate,
                    total_tco2e: calcResult.co2_equivalent,
                    co2_equivalent: calcResult.co2_equivalent,
                    notes: updateData.notes,
                    factor_value_used: calcResult.factor_value,
                    factor_source: calcResult.factor_source,
                    financial_year: primaryFY
                })
                .eq('id', emissionId);

            if (updateError) throw updateError;

            await supabase.from('entry_history').insert({
                id: crypto.randomUUID(),
                emission_entry_id: emissionId,
                organisation_id: currentOrg.id,
                user_id: internalUserData?.id,
                action: 'updated',
                category: updateData.category,
                quantity: parseFloat(updateData.quantity),
                unit: categoryInfo?.unit || '',
                co2_equivalent: calcResult.co2_equivalent,
                entry_date: updateData.entry_date,
                notes: updateData.notes,
                user_email: user?.email,
                changes_json: JSON.stringify({
                    previous: {
                        category: originalEntry.category,
                        quantity: originalEntry.quantity,
                        notes: originalEntry.notes
                    },
                    new: {
                        category: updateData.category,
                        quantity: parseFloat(updateData.quantity),
                        notes: updateData.notes
                    }
                })
            });

            await supabase.from('audit_logs').insert({
                id: crypto.randomUUID(),
                user_id: internalUserData?.id,
                user_email: user?.email,
                organisation_id: currentOrg.id,
                resource_type: 'emission',
                resource_id: emissionId,
                action: 'update',
                description: `Updated ${categoryInfo?.label || updateData.category} entry: ${updateData.quantity} ${categoryInfo?.unit || ''}`
            });

            toast.success('Entry updated successfully!');
            if (onSuccess) onSuccess();
            fetchEmissionsWithDocs();
        } catch (error) {
            console.error('Error updating entry:', error);
            toast.error(error.message || 'Failed to update entry');
        } finally {
            setEditModalLoading(false);
        }
    };

    return {
        emissions,
        loading,
        deletingEntry,
        historyLoading,
        entryHistory,
        editModalLoading,
        fetchEmissionsWithDocs,
        handleDeleteEntry,
        fetchEntryHistory,
        addEmissionEntry,
        updateEmissionEntry
    };
};
